import React, { useEffect, useState } from "react";
import { Card, Table, Row, Col, Button, Alert } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { getExerciseRequest, approveExerciseRequest } from "../../../apis/options/EmployeeApi";
import Dollars from "../../common/typeFormatting/Dollars";
import IntegerFmt from "../../common/typeFormatting/IntegerFmt";
import DateFmt from "../../common/typeFormatting/Date";

const ExerciseRequestApprovalPage = () => {
    const { exerciseRequestId } = useParams();
    const history = useHistory();
    const [exerciseRequest, setExerciseRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processSuccess, setProcessSuccess] = useState(false);
    const [processError, setProcessError] = useState("");

    useEffect(() => {
        async function loadExerciseRequest() {
            try {
                const request = await getExerciseRequest(exerciseRequestId);
                setExerciseRequest(request);
                setIsLoading(false);
            } catch (error) {
                console.error("Error loading exercise request:", error);
                setProcessError("Failed to load exercise request.");
                setIsLoading(false);
            }
        }

        if (exerciseRequestId) {
            loadExerciseRequest();
        }
    }, [exerciseRequestId]);

    const handleApprove = async () => {
        setIsProcessing(true);
        setProcessError("");
        
        try {
            // TODO: Get actual user from authentication context
            await approveExerciseRequest(exerciseRequestId, "Current User");
            
            setProcessSuccess(true);
            setTimeout(() => {
                history.push(`/options/employees/${exerciseRequest.employeeId}`);
            }, 2000);
            
        } catch (error) {
            console.error("Error approving exercise request:", error);
            setProcessError("An error occurred while approving the request. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = () => {
        // TODO: Implement rejection functionality
        setProcessError("Rejection functionality not yet implemented.");
    };

    if (isLoading) {
        return <Row><Col>Loading exercise request...</Col></Row>;
    }

    if (!exerciseRequest) {
        return <Row><Col>Exercise request not found.</Col></Row>;
    }

    return (
        <Row>
            <Col>
                <Card style={{marginTop: 25}}>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Card.Title>
                                Exercise Request - {exerciseRequest.exerciseRequestId}
                            </Card.Title>
                            <Button 
                                variant="secondary" 
                                onClick={() => history.push(`/options/employees/${exerciseRequest.employeeId}`)}
                            >
                                Back to Employee Statement
                            </Button>
                        </div>

                        {processSuccess && (
                            <Alert variant="success">
                                Exercise request approved and executed successfully! Redirecting...
                            </Alert>
                        )}

                        {processError && (
                            <Alert variant="danger">
                                {processError}
                            </Alert>
                        )}

                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="text-muted mb-2">Status</div>
                                <h5><span className="badge bg-warning">{exerciseRequest.status}</span></h5>
                            </div>
                            <div className="col-md-3">
                                <div className="text-muted mb-2">Request Date</div>
                                <h6><DateFmt>{exerciseRequest.requestDate}</DateFmt></h6>
                            </div>
                            <div className="col-md-3">
                                <div className="text-muted mb-2">Total Shares</div>
                                <h5><IntegerFmt>{exerciseRequest.lineItems.reduce((sum, item) => sum + item.sharesToExercise, 0)}</IntegerFmt></h5>
                            </div>
                            <div className="col-md-3">
                                <div className="text-muted mb-2">Net Amount</div>
                                <h5><Dollars>{exerciseRequest.lineItems.reduce((sum, item) => {
                                    const cashlessShares = item.cashlessShares || 0;
                                    const purchaseShares = item.purchaseShares || 0;
                                    const marketPrice = item.marketPriceAtRequest || 0;
                                    const strikePrice = item.strikePriceAtRequest || 0;

                                    const proceeds = cashlessShares * Math.max(0, marketPrice - strikePrice);
                                    const purchaseCost = purchaseShares * strikePrice;
                                    return sum + (proceeds - purchaseCost);
                                }, 0)}</Dollars></h5>
                            </div>
                        </div>

                        {exerciseRequest.notes && (
                            <div className="mb-4">
                                <div className="text-muted mb-2">Notes</div>
                                <p>{exerciseRequest.notes}</p>
                            </div>
                        )}

                        <Table hover responsive style={{fontSize: 14}}>
                            <thead>
                                <tr>
                                    <th>Grant</th>
                                    <th className="text-center">Cashless Exercise</th>
                                    <th className="text-center">Purchase Exercise</th>
                                    <th className="text-center">Shares to Exercise</th>
                                    <th className="text-center">Strike Price</th>
                                    <th className="text-center">Market Price</th>
                                    <th className="text-center">Proceeds</th>
                                    <th className="text-center">Purchase Cost</th>
                                    <th className="text-center">Net Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {exerciseRequest.lineItems.map((item, index) => {
                                    const cashlessShares = item.cashlessShares || 0;
                                    const purchaseShares = item.purchaseShares || 0;
                                    const marketPrice = item.marketPriceAtRequest || 0;
                                    const strikePrice = item.strikePriceAtRequest || 0;

                                    const proceeds = cashlessShares * Math.max(0, marketPrice - strikePrice);
                                    const purchaseCost = purchaseShares * strikePrice;
                                    const netAmount = proceeds - purchaseCost;

                                    return (
                                        <tr key={index}>
                                            <td>{item.grantLabel}</td>
                                            <td className="text-center"><IntegerFmt>{cashlessShares}</IntegerFmt></td>
                                            <td className="text-center"><IntegerFmt>{purchaseShares}</IntegerFmt></td>
                                            <td className="text-center"><IntegerFmt>{item.sharesToExercise}</IntegerFmt></td>
                                            <td className="text-center"><Dollars>{strikePrice}</Dollars></td>
                                            <td className="text-center"><Dollars>{marketPrice}</Dollars></td>
                                            <td className="text-center"><Dollars>{proceeds}</Dollars></td>
                                            <td className="text-center"><Dollars>{purchaseCost}</Dollars></td>
                                            <td className="text-center"><Dollars>{netAmount}</Dollars></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        {exerciseRequest.status === "Pending" && (
                            <div className="d-flex justify-content-end gap-3 mt-4">
                                <Button
                                    variant="outline-danger"
                                    size="lg"
                                    onClick={handleReject}
                                    disabled={isProcessing}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={handleApprove}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? "Processing..." : "Approve & Execute"}
                                </Button>
                            </div>
                        )}

                        {exerciseRequest.status !== "Pending" && (
                            <Alert variant="info" className="mt-4">
                                This exercise request has already been processed and cannot be modified.
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ExerciseRequestApprovalPage;